function isSameOrigin(url) {
    try {
        return new URL(url, document.baseURI).origin === window.location.origin;
    } catch {
        return false;
    }
}

let missingTokenWarned = false;

function getCsrfToken(url) {
    if (!isSameOrigin(url)) return null;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (token && token.trim()) return token;
    if (!missingTokenWarned) {
        missingTokenWarned = true;
        console.warn('CSRF: meta[name="csrf-token"] is missing or empty; sending without a token.');
    }
    return null;
}

// Call immediately before a POST, while keeping each transport's body format.
// Read metadata afresh for retries and requests delayed by CAPTCHA.
export function withCsrfToken(url, data = {}) {
    const isFormData = data instanceof FormData;
    const isParams = data instanceof URLSearchParams;
    const isObject = data !== null && typeof data === 'object' &&
        (Object.getPrototypeOf(data) === Object.prototype || Object.getPrototypeOf(data) === null);
    if (!isFormData && !isParams && !isObject) {
        throw new TypeError('CSRF body must be a plain object, URLSearchParams or FormData. Convert serialized strings to URLSearchParams.');
    }
    const token = getCsrfToken(url);
    if (!token) return data;
    if (isFormData) {
        const result = new FormData();
        data.forEach((value, key) => result.append(key, value));
        result.set('csrf_token', token);
        return result;
    }
    if (isParams) {
        const result = new URLSearchParams(data);
        result.set('csrf_token', token);
        return result;
    }
    return {...data, csrf_token: token};
}

let formsInitialized = false;

function submissionToken(form, submitter) {
    // IDL normalizes invalid/empty enumerated values to GET, not the form method.
    // A control named "method" shadows form.method; call the native getter.
    const formMethod = Object.getOwnPropertyDescriptor(HTMLFormElement.prototype, 'method').get.call(form);
    const method = submitter?.formMethod || formMethod || 'get';
    if (method !== 'post') return null;
    const action = submitter?.getAttribute('formaction') ?? form.getAttribute('action');
    return getCsrfToken(action || window.location.href);
}

function initLegacyForms() {
    const cleanups = new WeakMap();
    const clicked = new WeakMap();
    // Older browsers may also lack SubmitEvent.submitter.
    document.addEventListener('click', event => {
        const button = event.target.closest?.('button, input[type="submit"], input[type="image"]');
        if (!button?.form || !['submit', 'image'].includes(button.type)) return;
        const form = button.form;
        clicked.set(form, button);
        setTimeout(() => {if (clicked.get(form) === button) clicked.delete(form);}, 0);
    }, true);
    document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        cleanups.get(form)?.();
        if (event.defaultPrevented) return;
        const token = submissionToken(form, event.submitter || clicked.get(form));
        if (!token) return;
        const existing = Array.from(form.elements).filter(field => field.name === 'csrf_token');
        const disabled = existing.map(field => field.disabled);
        existing.forEach(field => {field.disabled = true;});
        const field = document.createElement('input');
        field.type = 'hidden'; field.name = 'csrf_token'; field.value = token;
        form.appendChild(field);
        const cleanup = () => {
            field.remove();
            existing.forEach((field, i) => {field.disabled = disabled[i];});
            if (cleanups.get(form) === cleanup) cleanups.delete(form);
        };
        cleanups.set(form, cleanup);
        // A timer also cleans up cancelled submissions. submit() without an event
        // on these browsers still needs a server-rendered hidden field.
        setTimeout(() => {if (cleanups.get(form) === cleanup) cleanup();}, 0);
    }, true);
}

export function initCsrfForms() {
    if (formsInitialized) return;
    formsInitialized = true;
    if (!('onformdata' in HTMLFormElement.prototype)) {
        initLegacyForms();
        return;
    }
    const submissions = new WeakMap();

    // formdata has no submitter. Remember it during this synchronous submission
    // so button-specific formaction/formmethod are respected, then discard it.
    document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        submissions.set(form, event);
        // A trusted click can run microtasks between submit and formdata.
        // Keep the submitter until the next task instead.
        setTimeout(() => {
            if (submissions.get(form) === event) submissions.delete(form);
        }, 0);
    }, true);

    // Capture also covers newly inserted forms, FormData(form), and submit(),
    // which bypasses the submit event. No persistent hidden field can leak to GET.
    document.addEventListener('formdata', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const submission = submissions.get(form);
        // Consume this submission's context before another synchronous submit().
        submissions.delete(form);
        const submitter = submission && !submission.defaultPrevented ? submission.submitter : null;
        const token = submissionToken(form, submitter);
        if (token) event.formData.set('csrf_token', token);
    }, true);
}
